/**
 * Grupos de WhatsApp (Fase 4): lista os grupos da instância (WAPI da TDZAIN) e
 * permite responder a UM grupo por vez. Não existe envio em massa: o formulário
 * é de um único grupo, com confirmação explícita.
 */
// Import explícito de `React` — ver o mesmo comentário em `AppShell.tsx`.
import React, { useState, type ReactElement } from "react";
import { Users } from "lucide-react";
import { StatusPanel, errorDetail } from "../components/StatusPanel.js";
import { Avatar, Badge, Button, EmptyState, Field, PageHeader, controlClass } from "../ui/index.js";
import { useWhatsAppGroupSend, useWhatsAppGroups } from "../queries.js";
import type { WhatsAppGroup } from "../api/types.js";

export function GruposView({ groups }: { groups: WhatsAppGroup[] }): ReactElement {
  const send = useWhatsAppGroupSend();
  const [selected, setSelected] = useState<string>("");
  const [text, setText] = useState("");
  const group = groups.find((item) => item.id === selected);
  return (
    <div className="page flex flex-col gap-6" data-page="grupos">
      <PageHeader title="Grupos de WhatsApp" description="Grupos da instância WAPI da TDZAIN. A resposta vai para um único grupo por vez." />
      {groups.some((item) => item.simulated) ? (
        <p className="m-0 rounded-panel border border-warning bg-warning-soft px-4 py-3 text-sm font-bold text-ink" data-source="simulator">
          Dados do simulador: o WAPI real ainda não está configurado neste ambiente.
        </p>
      ) : null}
      {groups.length === 0 ? (
        <EmptyState icon={Users} title="Nenhum grupo encontrado" description="A instância não participa de nenhum grupo." />
      ) : (
        <ul className="m-0 grid list-none grid-cols-1 gap-4 p-0 md:grid-cols-2 xl:grid-cols-3" aria-label="Grupos">
          {groups.map((item) => (
            <li
              key={item.id}
              className={"flex flex-col gap-3 rounded-panel border bg-surface p-4 shadow-s1 " + (item.id === selected ? "border-brand ring-1 ring-brand" : "border-border")}
              data-group-id={item.id}
            >
              <div className="flex items-center gap-3">
                <Avatar name={item.name} />
                <div className="flex min-w-0 flex-col">
                  <span className="truncate text-base font-extrabold text-ink">{item.name}</span>
                  <span className="text-xs text-muted">{item.size !== null ? `${item.size} participantes` : "Participantes não informados"}</span>
                </div>
                {item.simulated ? <Badge tone="warning">Simulador</Badge> : <Badge tone="success">WhatsApp</Badge>}
              </div>
              <Button onClick={() => setSelected(item.id)}>Responder</Button>
            </li>
          ))}
        </ul>
      )}
      {group ? (
        <form
          className="flex flex-col gap-3 rounded-panel border border-border bg-surface p-5 shadow-s1"
          aria-label={`Responder ao grupo ${group.name}`}
          onSubmit={(event) => {
            event.preventDefault();
            const value = text.trim();
            if (!value) return;
            send.mutate({ groupId: group.id, text: value }, { onSuccess: () => setText("") });
          }}
        >
          <Field label={`Mensagem para o grupo "${group.name}"`}>
            {({ id }) => <textarea id={id} className={controlClass + " min-h-28 py-2"} value={text} onChange={(e) => setText(e.target.value)} maxLength={4000} />}
          </Field>
          <Button type="submit" variant="primary" className="self-end" disabled={send.isPending || !text.trim()}>
            {send.isPending ? "Enviando…" : "Enviar ao grupo"}
          </Button>
          {send.isSuccess ? <p className="m-0 text-sm font-bold text-success" role="status">Mensagem enviada ao grupo.</p> : null}
          {send.error ? <p className="m-0 text-sm font-bold text-danger" role="alert">{`Não foi possível enviar: ${send.error.message}`}</p> : null}
        </form>
      ) : null}
    </div>
  );
}

export function Grupos(): ReactElement {
  const query = useWhatsAppGroups();
  if (query.isPending) return <StatusPanel kind="loading" title="Carregando grupos…" />;
  if (query.error) return <StatusPanel kind="error" title="Não foi possível carregar os grupos" detail={errorDetail(query.error)} />;
  return <GruposView groups={query.data ?? []} />;
}
